import React from "react";
import {observer} from "mobx-react";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import {FONT_COLOR, FONT_VARIANT} from "../../../../../components/fonts/font-props";
import Card from "../../../../../components/layouts/card/card";
import RmaShipmentStore from "../../stores/rma-shipment-store";
import { RETURN_METHOD_TYPE } from "../../../../return-methods/return-method-types";
import ShipmentWithPrePaidLabel from "./components/shipment-with-pre-paid-label/shipment-with-pre-paid-label";
import ShipmentWithAnyCarrier from "./components/shipment-with-any-carrier/shipment-with-any-carrier";
import ShipWithToDropOffLocation from "./components/shipment-to-drop-off-location/shipment-to-drop-off-location";
import NoShipmentRequired from "./components/no-shipment-required/no-shipment-required";
import CustomerCaseStore from "../../stores/customer-case-store";
import RmaPageStore from "../../stores/rma-page-store";
import dashboardTranslationsStore from "../../../../../dashboard-translation/stores/dashboard-translations-store";
import { CARD_SPACING_VARIANT } from "../../../../../components/layouts/card/card.types";
import ShipmentWithAnyCarrierWithLabel from "./components/ship-with-any-carrier-with-label/ship-with-any-carrier-with-label";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import showSbrPageStore from "../../../rma-list-page/stores/show-sbr-page.store";
import SBRPromoText from "./components/sbr-promo-text/sbr-promo-text";
import rmsMixpanelStore from "../../../../../stores/rms-mixpanel-store";

interface IProps{
	shipmentsList: RmaShipmentStore[];
    customerCaseStore: CustomerCaseStore | undefined;
    rmaPageStore: RmaPageStore;
}

const ShippingLineComponent: React.FC<{ shipment: RmaShipmentStore, rmaPageStore: RmaPageStore }> = ({shipment, rmaPageStore}) => {
    if (shipment.returnMethodType === undefined) {
       console.log('ERROR: Should never happen, each shipping belongs to some return method');
       return <></>;
    }

    switch (shipment.returnMethodType) {
        case RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL:
            return <ShipmentWithPrePaidLabel shipment={shipment} rmaPageStore={rmaPageStore} />
        case RETURN_METHOD_TYPE.SHIP_WITH_ANY_CARRIER:
            const isLabelGeneratedViaReturngoApi = !!(shipment.returnLabelUrl || shipment.qrImageUrl);
            /** When the label is added to a return method of type ‘Ship with any carrier' via the API, replace the fields we present for this RM type with those we present for RMs of type 'Ship with Pre-paid Label'. */
            if (isLabelGeneratedViaReturngoApi) {
                return <ShipmentWithAnyCarrierWithLabel shipment={shipment} rmaPageStore={rmaPageStore} />;
            }        
            return <ShipmentWithAnyCarrier shipment={shipment} rmaPageStore={rmaPageStore} />
        case RETURN_METHOD_TYPE.DROP_OFF_LOCATION:
            return <ShipWithToDropOffLocation shipment={shipment} rmaPageStore={rmaPageStore} />
        default:
            console.log('ERROR: Should never happen, shipping could belong to RM of types SHIP_WITH_PRE_PAID_LABEL, SHIP_WITH_ANY_CARRIER, DROP_OFF_LOCATION only');
            return <></>;
    };
};

const RmaPageShipmentsCard:React.FC<IProps> = observer(({shipmentsList, customerCaseStore, rmaPageStore})=>{
    const [isExpanded, setIsExpanded] = React.useState(false);

    const { getTranslation } = useDashboardTranslation();
    const scope = 'dashboard-returns-rma-card';

	const onShipmentsExpand = (expanded: boolean) => {
		if (expanded) {
			rmsMixpanelStore.trackEvent("d&_shipments_dropdown", { RMAID: rmaPageStore.rmaId });
		}
		setIsExpanded(expanded);
	};

	return (
        <Card
            toolbar={{
                title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation("SHIPMENTS_CARD_TITLE", scope)}</FontBody1>,
                content: showSbrPageStore.show && <SBRPromoText />
            }}
            isExpandable={true}
            isExpanded={isExpanded}
            expandCallback={onShipmentsExpand}
            spacingVariant={CARD_SPACING_VARIANT.SMAll}
        >
            {!dashboardTranslationsStore.isLoading && shipmentsList.map((shipment, index) => (
                <div className="shipment-line-container" key={`rma_page_shipment_item_${index}`}>
                    <ShippingLineComponent shipment={shipment} rmaPageStore={rmaPageStore} />
                </div>
            ))}
            
            {customerCaseStore && <NoShipmentRequired totalNoShipmentRequiredItems={customerCaseStore.getTotalNoShipmentRequiredItems()} />}
        </Card>
	);
})

export default RmaPageShipmentsCard;
